import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const OPT = {
  videos: ["Videos", "Vidéos"], exercises: ["Exercises", "Exercices"], flash_cards: ["Flash cards", "Flash cards"], stories: ["Stories", "Histoires"],
  kiko_stars: ["Kiko & stars", "Kiko et les étoiles"], yes: ["Yes", "Oui"], a_bit_high: ["A bit high", "Un peu cher"], too_high: ["Too high", "Trop cher"],
};

// Reading pilot survey for parents (8 questions, bilingual).
export default function ReadingSurvey() {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [ans, setAns] = useState({});
  const [sent, setSent] = useState(false);
  useEffect(() => { api.get("/reading-survey").then(({ data }) => { setD(data); setSent(data.done); }).catch(() => navigate("/login")); }, [navigate]);
  if (!d) return null;
  const submit = async () => {
    try {
      await api.post("/reading-survey", { answers: ans });
      setSent(true);
    } catch (e) {
      toast.error("Please answer every question · Merci de répondre à toutes les questions");
    }
  };
  return (
    <div className="min-h-screen bg-[#F8FAFC] font-parent text-slate-900">
      <main className="max-w-2xl mx-auto px-5 py-8">
        <button onClick={() => navigate("/parent")} className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 mb-4" data-testid="rsurvey-back"><ArrowLeft size={16} /> Dashboard</button>
        <div className="flex items-center gap-3 mb-6">
          <img src="/mascot/kiko.png" alt="Kiko" className="w-16 h-16" />
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold">Reading with Kiko · Lecture avec Kiko</h1>
            <p className="text-slate-500">8 quick questions · 8 petites questions (2 min)</p>
          </div>
        </div>
        {sent ? (
          <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="rounded-2xl bg-green-50 border-2 border-green-300 p-6 text-center" data-testid="rsurvey-thanks">
            <p className="text-2xl font-bold">Thank you! · Merci ! 💚</p>
            <p className="text-slate-600 mt-1">Your answers help us improve Reading for every child. · Vos réponses nous aident à améliorer la Lecture.</p>
          </motion.div>
        ) : (
          <div className="space-y-4">
            {d.questions.map((q, k) => (
              <div key={q.id} className="rounded-2xl bg-white border border-slate-200 shadow-sm p-5" data-testid={`rsurvey-q-${q.id}`}>
                <p className="font-semibold">{k + 1}. {q.en}</p>
                <p className="text-sm text-slate-500 mb-3">{q.fr}</p>
                {q.type === "scale" && (
                  <div className="flex gap-2">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button key={n} onClick={() => setAns({ ...ans, [q.id]: n })} data-testid={`rsurvey-${q.id}-${n}`}
                        className={`w-11 h-11 rounded-full border-2 font-bold transition-colors ${ans[q.id] === n ? "bg-blue-600 text-white border-blue-600" : "border-slate-300 hover:border-slate-900"}`}>{n}</button>
                    ))}
                  </div>
                )}
                {q.type === "choice" && (
                  <div className="flex flex-wrap gap-2">
                    {q.options.map((o) => (
                      <button key={o} onClick={() => setAns({ ...ans, [q.id]: o })} data-testid={`rsurvey-${q.id}-${o}`}
                        className={`rounded-full border-2 px-4 py-2 text-sm font-semibold transition-colors ${ans[q.id] === o ? "bg-blue-600 text-white border-blue-600" : "border-slate-300 hover:border-slate-900"}`}>
                        {ans[q.id] === o && <Check size={12} className="inline mr-1" />}{OPT[o][0]} · {OPT[o][1]}
                      </button>
                    ))}
                  </div>
                )}
                {q.type === "text" && (
                  <textarea rows={3} value={ans[q.id] || ""} onChange={(e) => setAns({ ...ans, [q.id]: e.target.value })} data-testid={`rsurvey-${q.id}`}
                    className="w-full rounded-xl border-2 border-slate-200 p-3 focus:border-blue-500 outline-none" />
                )}
              </div>
            ))}
            <button onClick={submit} data-testid="rsurvey-submit" className="w-full rounded-full bg-blue-600 hover:bg-blue-700 text-white font-bold h-12 transition-colors">
              Send · Envoyer
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
